import React, { useCallback, useMemo } from 'react';
import { StyleSheet, View } from 'react-native';
import { theme } from '@garnish/constants';
import { useResponsive } from '../../hooks';
import { PickersGroup } from '../Pickers/PickersGroup';
import { Select } from '../Select';
import { HStack } from '../Stack';
import { BodyText } from '../Text';

// ────────────────────────────────────────────────────────────────────────────────

export const BirthdayPicker = (props: BirthdayPickerProps) => {
  const {
    value,
    onChange,
    helperText,
    label,
    monthLabel = 'Month',
    dayLabel = 'Day',
    monthNames = generateMonthNamesList(),
  } = props;
  const { currentBreakpoint } = useResponsive();

  // ─── HELPERS ────────────────────────────────────────────────────────

  const optionsGroups = useMemo<OptionsGroup>(
    () => ({
      [monthLabel]: [{ value: '', label: dayLabel }],
      ...generateMonthsAndDatesGroups(monthNames),
    }),
    [dayLabel, monthNames, monthLabel],
  );

  // ────────────────────────────────────────────────────────────────────

  return (
    <View>
      {label ? (
        <BodyText size={5} style={styles.label}>
          {label}
        </BodyText>
      ) : null}

      {currentBreakpoint.isMobile ? (
        <MobileControl
          value={value}
          onChange={onChange}
          optionsGroups={optionsGroups}
        />
      ) : (
        <DesktopControl
          value={value}
          onChange={onChange}
          optionsGroups={optionsGroups}
        />
      )}

      {helperText ? (
        <BodyText size={5} style={styles.helperText}>
          {helperText}
        </BodyText>
      ) : null}
    </View>
  );
};

// ─── SUBCOMPONENTS ──────────────────────────────────────────────────────────────

const MobileControl = (props: ControlProps) => {
  const { value = '', onChange, optionsGroups } = props;

  return (
    <PickersGroup
      optionsGroups={optionsGroups}
      value={value}
      onSubmit={onChange}
      variation="box"
    />
  );
};

const DesktopControl = (props: ControlProps) => {
  const { value = '', onChange, optionsGroups } = props;

  // ────────────────────────────────────────────────────────────────────

  // extract month options from month and dates groups
  const monthsOptions = useMemo(
    () => Object.keys(optionsGroups).map((label) => ({ value: label, label })),
    [optionsGroups],
  );

  const [selectedMonthValue] = (value as string).split('/');
  const selectedMonthIndex = Number(selectedMonthValue || 0);
  const selectedMonthLabel = monthsOptions[selectedMonthIndex].label;
  const selectedMonthDates = optionsGroups[selectedMonthLabel];

  // ────────────────────────────────────────────────────────────────────

  const onMonthChange = useCallback(
    (monthLabel) => {
      const firstAvailableDate = optionsGroups[monthLabel][0];

      onChange(firstAvailableDate.value);
    },
    [onChange, optionsGroups],
  );

  // ────────────────────────────────────────────────────────────────────

  return (
    <HStack itemsPerRow={2}>
      <Select
        value={selectedMonthLabel}
        options={monthsOptions}
        onSelect={onMonthChange}
        variation="box"
      />

      <Select
        value={value}
        options={selectedMonthDates}
        onSelect={onChange}
        variation="box"
      />
    </HStack>
  );
};

// ─── TYPES ──────────────────────────────────────────────────────────────────────

type BirthdayPickerProps = {
  readonly helperText?: string;
  readonly label?: string;
  readonly monthLabel?: string;
  readonly dayLabel?: string;
  readonly monthNames?: readonly string[];
} & Pick<ControlProps, 'value' | 'onChange'>;

type ControlProps = {
  readonly value: string | number;
  readonly onChange: (value?: string | number) => void;
  readonly optionsGroups: OptionsGroup;
};

type OptionsGroup = Record<
  string,
  ReadonlyArray<{ readonly value: string; readonly label: string }>
>;

// ─── STYLES ─────────────────────────────────────────────────────────────────────

const styles = StyleSheet.create({
  label: {
    paddingBottom: theme.spacing.medium,
  },
  helperText: {
    paddingTop: theme.spacing.medium,
  },
});

// ─── UTILS ──────────────────────────────────────────────────────────────────────

export const generateMonthsAndDatesGroups = (
  monthNames: readonly string[],
): OptionsGroup => {
  return Object.fromEntries(
    monthNames.map((monthName, index) => [
      monthName,
      generateMonthDatesOptions(index),
    ]),
  );
};

export const generateMonthDatesOptions = (monthIndex: number) => {
  const monthMaxDate = getMonthLastDay(monthIndex);

  return Array.from({ length: monthMaxDate }).map((_, index) => {
    const month = `${monthIndex + 1}`;
    const monthValue = month.padStart(2, '0');
    const date = `${index + 1}`;
    const dateValue = date.padStart(2, '0');

    return { value: `${monthValue}/${dateValue}`, label: date };
  });
};

export const getMonthLastDay = (monthIndex: number) => {
  const leapYear = 1752;

  return new Date(leapYear, monthIndex + 1, 0).getDate();
};

export const generateMonthNamesList = () => {
  return [...Array.from({ length: 12 })].map((_, index) =>
    new Date(0, index).toLocaleString('en', { month: 'long' }),
  );
};
